"use client";

import type {
  PlatformBusiness,
  PlatformConsumer,
  PlatformTotals,
} from "@/modules/insights/surfaces";
import { DataList, type Column } from "./DataList";
import { DataUnavailable } from "./DataUnavailable";
import { StatTile } from "./ui";

/**
 * AdminPlatformScreen (docs/ux/screens/AdminPlatformScreen.md), shared by both
 * frontends (R22 T15).
 *
 * Everything on Bookr across every business. Admin only, no business scope, and
 * **read-only** over the Insights read model, which is why the whole of it
 * moves without a single command.
 *
 * Two decisions worth keeping as they are. The lists use `DataList` rather than
 * a wide table in `overflow-x-auto`: nothing in this app scrolls horizontally,
 * so it is a real table from `sm` up and stacked label/value cards below it.
 * And the booking-state breakdown is a definition list rather than a run-on
 * sentence, because seven numbers in prose is a paragraph nobody reads.
 *
 * **A Consumer is listed by name and never by phone**, unlike the clinic's own
 * customer directory: a business has a relationship with the people who booked
 * with it, and the platform view has none with anybody.
 */
export function AdminPlatform({
  totals,
  businesses,
  consumers,
  configured = true,
}: {
  /** Null when the read failed. */
  totals: PlatformTotals | null;
  businesses: PlatformBusiness[];
  consumers: PlatformConsumer[];
  configured?: boolean;
}) {
  return (
    <>
        <header className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight text-ink">Platform</h1>
          <p className="mt-1 text-sm text-muted">
            Everything on Bookr, across every business.
          </p>
        </header>

        {totals === null ? (
          <DataUnavailable configured={configured} what="the platform figures" />
        ) : (
          <div className="space-y-10">
            <Totals totals={totals} />
            <BusinessesList businesses={businesses} />
            <ConsumersList consumers={consumers} />
          </div>
        )}
    </>
  );
}

function Totals({ totals }: { totals: PlatformTotals }) {
  const b = totals.bookings;
  const states: [string, number][] = [
    ["Requested", b.requested],
    ["Approved", b.approved],
    ["Completed", b.completed],
    ["Cancelled", b.cancelled],
    ["Declined", b.declined],
    ["Withdrawn", b.withdrawn],
    ["Expired", b.expired],
  ];
  return (
    <section>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatTile label="Businesses" value={String(totals.businesses)} />
        <StatTile label="Customers" value={String(totals.consumers)} />
        <StatTile
          label="Active practitioners"
          value={String(totals.activePractitioners)}
        />
        <StatTile label="Reviews" value={String(totals.reviews)} />
        <StatTile
          label="Bookings"
          value={String(b.total)}
          hint={`${b.completed} completed · ${b.approved} upcoming`}
        />
      </div>

      <h2 className="mb-2 mt-6 text-xs font-bold uppercase tracking-wide text-muted">
        Bookings by state
      </h2>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-4">
        {states.map(([label, value]) => (
          <div key={label} className="flex items-baseline justify-between gap-2">
            <dt className="text-sm text-body">{label}</dt>
            <dd className="font-mono text-sm font-semibold text-ink">{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function BusinessesList({ businesses }: { businesses: PlatformBusiness[] }) {
  const columns: Column<PlatformBusiness>[] = [
    { key: "name", header: "Business", cell: (b) => b.name, primary: true },
    {
      key: "categories",
      header: "Categories",
      cell: (b) => b.categoryNames.join(", ") || "None",
    },
    {
      key: "confirmed",
      header: "Confirmed",
      align: "end",
      numeric: true,
      cell: (b) => b.confirmedCount,
    },
    {
      key: "rating",
      header: "Rating",
      align: "end",
      numeric: true,
      cell: (b) =>
        b.rating.count > 0
          ? `${b.rating.average!.toFixed(1)} ★ (${b.rating.count})`
          : "No rating",
    },
    {
      key: "approved",
      header: "Approved",
      align: "end",
      numeric: true,
      cell: (b) => new Date(b.approvedAt).toLocaleDateString(),
    },
  ];
  return (
    <DataList
      title="Businesses"
      count={businesses.length}
      rows={businesses}
      rowKey={(b) => b.id}
      columns={columns}
      empty="No businesses yet."
    />
  );
}

function ConsumersList({ consumers }: { consumers: PlatformConsumer[] }) {
  const columns: Column<PlatformConsumer>[] = [
    { key: "name", header: "Customer", cell: (c) => c.displayName, primary: true },
    {
      key: "bookings",
      header: "Bookings",
      align: "end",
      numeric: true,
      cell: (c) => c.bookingCount,
    },
    {
      key: "joined",
      header: "Joined",
      align: "end",
      numeric: true,
      cell: (c) => new Date(c.joinedAt).toLocaleDateString(),
    },
  ];
  return (
    <DataList
      title="Customers"
      count={consumers.length}
      rows={consumers}
      rowKey={(c) => c.id}
      columns={columns}
      empty="No customers yet."
    />
  );
}
